import { Capacitor } from '@capacitor/core'

/**
 * Returns true if the native calendar plugin is available (native platform only).
 */
export function isCalendarAvailable() {
  return Capacitor.isNativePlatform()
}

/**
 * Requests full calendar access on native platforms.
 * Returns 'granted' or 'denied'.
 */
export async function requestCalendarPermission() {
  if (!isCalendarAvailable()) return 'denied'

  const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar')
  try {
    const { result } = await CapacitorCalendar.requestFullCalendarAccess()
    return result === 'granted' ? 'granted' : 'denied'
  } catch {
    return 'denied'
  }
}

/**
 * Checks the current calendar permission state without prompting.
 * Returns a Capacitor PermissionState: 'granted', 'denied', 'prompt', or 'prompt-with-rationale'.
 */
export async function checkCalendarPermission() {
  if (!isCalendarAvailable()) return 'denied'

  const { CapacitorCalendar, CalendarPermissionScope } = await import('@ebarooni/capacitor-calendar')
  try {
    const { result } = await CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.READ_CALENDAR })
    return result
  } catch {
    return 'denied'
  }
}

/**
 * Opens the app's page in the native iOS Settings so the user can grant
 * calendar access that was previously denied (iOS won't re-prompt in-app).
 * Best-effort: Capacitor forwards the `app-settings:` scheme to the system.
 */
export function openNativeSettings() {
  if (!isCalendarAvailable()) return
  try {
    window.open('app-settings:', '_system')
  } catch {
    try { window.location.href = 'app-settings:' } catch {}
  }
}

/**
 * Fetches calendar events for a given date and time range.
 * Returns an array of schedule-item-shaped objects with type 'event'.
 * Excludes all-day events and events that fall entirely outside the day window.
 *
 * Events that only partially overlap the window (e.g. a 7:30–9:00 class when the
 * day starts at 8:00) are kept: `time`/`endTime` are clamped to the window so all
 * scheduling logic (planner, conflict detection, gap-closing) stays inside the
 * day, while the true times are preserved in `displayTime`/`displayEndTime` so
 * the UI can show that the event spills past the boundary instead of misreporting
 * it. Display overrides are only set for spills that stay on the same calendar day.
 *
 * @param {string} dateStr - YYYY-MM-DD format
 * @param {string} dayStart - HH:MM format (e.g., '08:00')
 * @param {string} dayEnd - HH:MM format (e.g., '17:00')
 * @returns {Promise<Array>} Schedule items with type 'event'
 * @throws if the native calendar read fails — callers surface this to the user
 *   rather than silently showing an empty calendar.
 */
export async function fetchCalendarEvents(dateStr, dayStart, dayEnd) {
  if (!isCalendarAvailable()) return []

  const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar')

  const startDate = new Date(`${dateStr}T${dayStart}:00`)
  const endDate = new Date(`${dateStr}T${dayEnd}:00`)
  const dayMidnight = new Date(`${dateStr}T00:00:00`)
  const nextMidnight = new Date(dayMidnight.getTime() + 24 * 60 * 60 * 1000)

  const { result: events } = await CapacitorCalendar.listEventsInRange({
    from: startDate.getTime(),
    to: endDate.getTime(),
  })

  const fmt = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

  return (events || [])
    .filter(event => !event.isAllDay)
    .map(event => {
      const start = new Date(event.startDate)
      const end = new Date(event.endDate)

      // Drop events with no overlap with the day window (fully before or after).
      if (!(start < endDate && startDate < end)) return null

      // Clamp to the window for all scheduling logic.
      const clampedStart = start < startDate ? startDate : start
      const clampedEnd = end > endDate ? endDate : end

      const item = {
        time: fmt(clampedStart),
        endTime: fmt(clampedEnd),
        title: event.title || 'Calendar Event',
        type: 'event',
        taskId: null,
        subtaskId: null,
        location: event.location || null,
      }

      // Preserve true times for display when the event spills past the window on
      // the same day (multi-day spills keep the clamped time as the safe fallback).
      if (start < startDate && start >= dayMidnight && start < nextMidnight) {
        item.displayTime = fmt(start)
      }
      if (end > endDate && end > dayMidnight && end <= nextMidnight) {
        item.displayEndTime = fmt(end)
      }

      return item
    })
    .filter(item => item && item.time < item.endTime)
    .sort((a, b) => a.time.localeCompare(b.time))
}

/**
 * Fetches all-day events for a given date. Returns an array of event titles.
 * All-day events (holidays, trips, exam days) aren't placed into timed blocks —
 * the daily schedule surfaces them as a banner so they aren't silently ignored.
 * Queries the full calendar day so events that span midnight are caught.
 *
 * @param {string} dateStr - YYYY-MM-DD format
 * @returns {Promise<string[]>} All-day event titles
 * @throws if the native calendar read fails.
 */
export async function fetchAllDayEvents(dateStr) {
  if (!isCalendarAvailable()) return []

  const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar')

  const from = new Date(`${dateStr}T00:00:00`).getTime()
  const to = new Date(`${dateStr}T23:59:59`).getTime()

  const { result: events } = await CapacitorCalendar.listEventsInRange({ from, to })

  return (events || [])
    .filter(event => event.isAllDay)
    .map(event => String(event.title || 'All-day event').slice(0, 200))
}
